import * as Sentry from "@sentry/react"
import { envConfig } from "config/envConfig"
import { useEffect } from "react"
import {
	createRoutesFromChildren,
	matchRoutes,
	useLocation,
	useNavigationType,
} from "react-router"

export { wrapCreateBrowserRouterV7 } from "@sentry/react"

export const initSentryUser = Sentry.setUser
export const captureFeedback = Sentry.captureFeedback
export const lastEventId = Sentry.lastEventId

export function initSentry() {
	if (
		["test", "development"].includes(import.meta.env.MODE) ||
		import.meta.env.STORYBOOK === "true" ||
		navigator.userAgent.includes("HeadlessChrome") ||
		Sentry.isInitialized()
	) {
		return
	}

	const environment = (() => {
		const { host } = window.location
		if (
			[/localhost/, /link-pr/, /help-pr/].some((regexp) => regexp.test(host))
		) {
			return "local"
		} else if (/staging/.test(host)) {
			return "staging"
		}
		return "production"
	})()

	Sentry.init({
		environment,
		dsn: envConfig.sentryDsn(),
		release: envConfig.commitSHA(),
		// Setting this option to true will send default PII data to Sentry.
		// For example, automatic IP address collection on events
		sendDefaultPii: false,
		integrations: [
			Sentry.captureConsoleIntegration({ levels: ["error"] }),
			Sentry.httpClientIntegration({
				failedRequestStatusCodes: [
					[400, 499],
					[500, 599],
				],
			}),
			Sentry.browserTracingIntegration(),
			...(environment === "production" ? [Sentry.replayIntegration()] : []),
			Sentry.reactRouterV7BrowserTracingIntegration({
				useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
			}),
			Sentry.feedbackIntegration({ autoInject: false }),
		],
		// Tracing
		tracesSampleRate: 1.0, //  Capture 100% of the transactions
		// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
		tracePropagationTargets: [
			"localhost",
			/^https:\/\/backend(.*)empohealth\.com\/api/,
		],
		// Session Replay
		replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
		replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
	})
}
